import { SavedScope } from '@shared/models/saved-scope.model';
import { Project, ProjectMetadata } from '@shared/models/project.model';
import {
  getProjectMeta,
  updateBuilding,
} from '@shared/helpers/project.helpers';
import { ScopePreset, SCOPE_PRESETS } from './scope-presets.data';

/** Adapt an organization snapshot to the selector's preset/preview contract. */
export const savedScopeToPreset = (scope: SavedScope): ScopePreset => ({
  id: `saved:${scope.id}`,
  label: scope.name,
  lifecycles: scope.lifecycles,
  sbefGroups: scope.sbefGroups,
  genericClimateData: scope.conservative ? 'conservative' : 'typical',
  calculationPeriodYears: scope.calculationPeriodYears,
  target: scope.target,
});

/** Existing projects start with the built-in scopes; an explicit empty list stays empty. */
export const getEnabledScopeIds = (meta: ProjectMetadata): string[] =>
  meta.enabled_scope_ids ?? SCOPE_PRESETS.map(({ id }) => id);

/** Change only the project's available-preset list, retaining all calculation settings. */
export const withScopeEnabled = (
  project: Project,
  id: string,
  enabled: boolean,
): Project => {
  const meta = getProjectMeta(project);
  const current = getEnabledScopeIds(meta);
  const ids = withScopeIdEnabled(current, id, enabled);
  if (ids === current) return project;
  return updateBuilding(project, { meta: { ...meta, enabled_scope_ids: ids } });
};

/** Apply visibility to a project or organization list, retaining unknown IDs. */
export const withScopeIdEnabled = (
  ids: string[],
  id: string,
  enabled: boolean,
): string[] => {
  if (ids.includes(id) === enabled) return ids;
  return enabled ? [...ids, id] : ids.filter((candidate) => candidate !== id);
};

/** Retired definitions remain available only in projects already using them. */
export const getAvailableSavedScopes = (
  scopes: SavedScope[],
  enabledIds: string[],
  selectedId?: string,
): SavedScope[] =>
  scopes.filter(
    (scope) =>
      !scope.deleted ||
      enabledIds.includes(`saved:${scope.id}`) ||
      selectedId === `saved:${scope.id}`,
  );
